import React, { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import styled, { useTheme } from 'styled-components/native'

import {
  COMMON_MIXPANEL_EVENTS,
  COMMON_MIXPANEL_PROPERTIES,
  WELLNESS_CHECK_IN_EVENTS,
  WELLNESS_CHECK_IN_PAGES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { PathwayOptions } from '@lyrahealth-inc/shared-app-logic/src/features/pathways/constants'
import { WellbeingResponse } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/types'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { CheckMarkIconV2 } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CheckMarkIconV2'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Subhead } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/subhead/Subhead'
import { WellnessCheckInHeader } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/wellnessCheckInHeader/WellnessCheckInHeader'
import { WellnessCheckInCopyrightFooter } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/wellnessCheckInResultsFooter/WellnessCheckInCopyrightFooter'
import { WellnessCheckInResults } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/wellnessCheckInResults/WellnessCheckInResults'
import { SubheadSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { getWellnessCheckInEntryPoint } from './data/wellnessCheckInSelectors'
import { useGetLegalFooterProps } from '../../common/hooks/useGetLegalFooterProps'
import { useNavigateToWellnessCheckIn } from '../../common/hooks/useNavigateToWellnessCheckIn'
import { useWellnessCheckIn } from '../../common/hooks/useWellnessCheckIn'
import { logToSumoLogic } from '../../common/utils/userUtils'
import { trackEventWithObj } from '../../data/mixpanel'
import { useAppDispatch } from '../../data/storeConfiguration/store'
import { setSelectedPathwayOption } from '../register/data/registerActions'
import RegistrationEmailCaptureContainer from '../register/setupAccount/RegistrationEmailCaptureContainer'
import RegistrationOneTimePasscodeContainer from '../register/setupAccount/RegistrationOneTimePasscodeContainer'
import { VerifyEmailWellnessCheckInContainerProps } from '../register/setupAccount/VerifyEmailContainer'

const WellnessCheckInHeaderContainer = styled(Subhead)<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.breakpoints.isMobileSized ? theme.spacing['24px'] : theme.spacing['24px'],
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['24px'],
}))

const WellnessCheckInContainer = styled(View)<{ theme: ThemeType }>(({ theme }) => ({
  margin: `0 auto ${theme.spacing['32px']} auto`,
}))

const WellnessCheckInText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  color: theme.colors.textSecondary,
  marginLeft: theme.spacing['12px'],
}))

const WellnessCheckInTitleText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['8px'],
}))

const WellnessCheckInTextContainer = styled(View)<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  marginBottom: theme.spacing['8px'],
}))

const WellnessCheckInPreviewContainer: FunctionComponent = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const wellnessCheckInEntryPoint = useSelector(getWellnessCheckInEntryPoint)
  const { colors, breakpoints } = useTheme()
  const { formatMessage } = useIntl()
  const [wellbeingResponse, setWellbeingResponse] = useState<WellbeingResponse | null | undefined>(null)
  const [verifyEmailProps, setVerifyEmailProps] = useState<VerifyEmailWellnessCheckInContainerProps | null>(null)
  const { showWellnessCheckIn, storedWellnessCheckInStateResults } = useWellnessCheckIn()
  const { navigateToWellnessCheckInForm } = useNavigateToWellnessCheckIn()
  const isLoading = wellbeingResponse === null
  const legalFooterProps = useGetLegalFooterProps()

  useEffect(() => {
    dispatch(setSelectedPathwayOption(PathwayOptions.WELLNESS_CHECK_IN))
  }, [dispatch])

  const onSubmit = (email: string, error?: string) => {
    // Set email query parameter to be used by RegistrationOneTimePasscodeContainer
    const params = new URLSearchParams(location.search)
    params.set('email', email)
    navigate(`${location.pathname}?${params.toString()}`, { replace: true })
    setVerifyEmailProps({
      registeredEmail: email,
      emailStateError: error,
    })
  }

  const BENEFIT_MESSAGES = [
    formatMessage({
      defaultMessage: 'Mental wellness sessions',
      description: 'Wellness check in registration mental wellness sessions description',
    }),
    formatMessage({
      defaultMessage: 'Meditations, articles & more',
      description: 'Wellness check in registration meditation description',
    }),
    formatMessage({
      defaultMessage: '24/7 Care Navigator support',
      description: 'Wellness check in registration care support description',
    }),
  ]

  const verifyEmailHeaderElements = (
    <View testID={tID('WellnessCheckInRegistrationHeader')}>
      <WellnessCheckInHeaderContainer
        size={breakpoints.isMobileSized ? SubheadSize.MEDIUM : SubheadSize.LARGE}
        text={formatMessage({
          defaultMessage: 'Sign up to see your full results',
          description: 'A title above the registration form for wellness check in',
        })}
        textAlign={'center'}
        testID={`${name}-title`}
      />
      <WellnessCheckInContainer>
        <WellnessCheckInTitleText
          text={formatMessage({
            defaultMessage: 'Your Lyra health account also includes:',
            description: 'Wellness check in registration title',
          })}
        />
        {BENEFIT_MESSAGES.map((benefitMessage, index) => {
          return (
            <WellnessCheckInTextContainer key={index}>
              <CheckMarkIconV2 fillColor={colors.iconActive} width={'24'} height={'24'} />
              <WellnessCheckInText text={benefitMessage} color={colors.textPrimary} />
            </WellnessCheckInTextContainer>
          )
        })}
      </WellnessCheckInContainer>
    </View>
  )

  const registrationContainer = verifyEmailProps ? (
    <RegistrationOneTimePasscodeContainer isPreview />
  ) : (
    <RegistrationEmailCaptureContainer
      isPreview
      previewSubmitCallback={onSubmit}
      previewComponents={verifyEmailHeaderElements}
    />
  )

  const registrationWithFooter = (
    <>
      {registrationContainer}
      <WellnessCheckInCopyrightFooter
        {...legalFooterProps}
        wellnessCheckInFooterBackgroundColor={colors.backgroundPrimary}
      />
    </>
  )

  const onEmptyStatePress = () => {
    dispatch(
      trackEventWithObj({
        event: WELLNESS_CHECK_IN_EVENTS.CLICK_WELLNESS_CHECK_IN_START_AGAIN,
        [COMMON_MIXPANEL_PROPERTIES.ACTION]: COMMON_MIXPANEL_EVENTS.CLICK,
        [COMMON_MIXPANEL_PROPERTIES.PAGE]: WELLNESS_CHECK_IN_PAGES.EMPTY_STATE_RESULTS,
      }),
    )
    navigateToWellnessCheckInForm({ entryPoint: wellnessCheckInEntryPoint })
  }

  useEffect(() => {
    if (!showWellnessCheckIn) {
      return
    }

    if (isLoading) {
      if (storedWellnessCheckInStateResults) {
        setWellbeingResponse(storedWellnessCheckInStateResults?.wellbeingResponse)
      } else {
        const errorMsg = 'Unable to find wellness check in from redux state in preview'
        console.error(errorMsg)
        logToSumoLogic('wellnessCheckInPreview', undefined, {
          message: errorMsg,
          type: 'error',
        })
        setWellbeingResponse(undefined)
      }
    }
  }, [isLoading, showWellnessCheckIn, storedWellnessCheckInStateResults])

  const handleOnLoad = useCallback(() => {
    dispatch(
      trackEventWithObj({
        event: `view ${WELLNESS_CHECK_IN_PAGES.PREVIEW} page`,
        [COMMON_MIXPANEL_PROPERTIES.ACTION]: COMMON_MIXPANEL_EVENTS.PAGE_LOAD,
      }),
    )
  }, [dispatch])

  const handleOnEmptyStateLoad = useCallback(() => {
    dispatch(
      trackEventWithObj({
        event: `view ${WELLNESS_CHECK_IN_PAGES.EMPTY_STATE_RESULTS}`,
        [COMMON_MIXPANEL_PROPERTIES.ACTION]: COMMON_MIXPANEL_EVENTS.PAGE_LOAD,
      }),
    )
  }, [dispatch])

  if (!showWellnessCheckIn) {
    return <></>
  }

  if (isLoading) {
    return <LoadingIndicator />
  }

  return (
    <>
      <WellnessCheckInHeader isResultsPage />
      <WellnessCheckInResults
        wellbeingResponse={wellbeingResponse}
        isSocialNeedEnabled={false}
        onEmptyStatePress={onEmptyStatePress}
        isPreview
        previewComponents={registrationWithFooter}
        footerComponents={<WellnessCheckInCopyrightFooter {...legalFooterProps} />}
        onLoad={handleOnLoad}
        onEmptyStateLoad={handleOnEmptyStateLoad}
      />
    </>
  )
}

export default WellnessCheckInPreviewContainer
